import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import legal from '../legal';
import { API, isDemo, DEMO_PAGES } from '../App';
import DemoControls from '../demo/DemoControls';
import { ONPREM } from '../edition';

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0f0f1a',
    display: 'flex',
  },
  sidebar: {
    width: '240px',
    backgroundColor: '#16213e',
    padding: '24px 0',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  sidebarLogo: {
    color: '#e94560',
    fontSize: '24px',
    fontWeight: 'bold',
    letterSpacing: '2px',
    padding: '0 24px',
    marginBottom: '8px',
  },
  sidebarSubtitle: {
    color: '#a0a0b0',
    fontSize: '11px',
    letterSpacing: '1px',
    padding: '0 24px',
    marginBottom: '32px',
    textTransform: 'uppercase',
  },
  navItem: {
    padding: '14px 24px',
    color: '#a0a0b0',
    fontSize: '15px',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'block',
    borderLeft: '3px solid transparent',
  },
  navItemActive: {
    padding: '14px 24px',
    color: '#ffffff',
    fontSize: '15px',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'block',
    borderLeft: '3px solid #e94560',
    backgroundColor: '#0f3460',
  },
  main: {
    flex: 1,
    padding: '40px',
    overflowY: 'auto',
  },
  pageTitle: {
    color: '#ffffff',
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  pageSubtitle: {
    color: '#a0a0b0',
    fontSize: '14px',
    marginBottom: '40px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '40px',
  },
  statCard: {
    backgroundColor: '#16213e',
    borderRadius: '16px',
    padding: '24px',
  },
  statNumber: {
    color: '#e94560',
    fontSize: '36px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  statLabel: {
    color: '#a0a0b0',
    fontSize: '14px',
  },
  sectionTitle: {
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '16px',
  },
  quickCard: {
    backgroundColor: '#16213e',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    textDecoration: 'none',
  },
  quickCardTitle: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '4px',
  },
  quickCardSubtitle: {
    color: '#a0a0b0',
    fontSize: '13px',
  },
  quickCardArrow: {
    color: '#e94560',
    fontSize: '24px',
  },
  logoutButton: {
    // Sits a short gap under the menu (not pinned to the bottom of long pages).
    marginTop: '48px',
    padding: '14px 24px',
    color: '#a0a0b0',
    fontSize: '15px',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    textAlign: 'left',
    width: '100%',
  },
};

const navItems = [
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Reports', path: '/reports' },
  { label: 'Dealers', path: '/dealers' },
  { label: 'Users', path: '/users', adminOnly: true },
  // One entry for all three benchmark pages; they share a tab bar (BenchmarkTabs).
  { label: 'Benchmarks', path: '/benchmark-mode' },
  { label: 'Survey', path: '/survey' },
  { label: 'Settings', path: '/settings', adminOnly: true },
  // On property the same page is the product key (License), not Stripe.
  { label: ONPREM ? 'License' : 'Billing', path: '/billing', adminOnly: true },
];

// On property: the trial / product-key notice, under the menu on every page,
// so nobody is surprised by read-only. Asked once every few minutes, not on
// every page change.
function LicenseNotice() {
  const [lic, setLic] = React.useState(() => {
    try {
      const c = JSON.parse(sessionStorage.getItem('ezpace_license') || 'null');
      return c && Date.now() - c.at < 300000 ? c.lic : null;
    } catch { return null; }
  });
  React.useEffect(() => {
    const changed = () => setLic(null);
    window.addEventListener('ezpace-license-changed', changed);
    return () => window.removeEventListener('ezpace-license-changed', changed);
  }, []);
  React.useEffect(() => {
    if (lic) return;
    const token = localStorage.getItem('admin_token');
    fetch(`${API}/license`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (!data) return;
        setLic(data);
        try { sessionStorage.setItem('ezpace_license', JSON.stringify({ at: Date.now(), lic: data })); } catch { /* ignore */ }
      })
      .catch(() => {});
  }, [lic]);
  if (!lic || !lic.notice) return null;
  return (
    <Link to="/billing" style={{
      display: 'block', margin: '16px 0', padding: '10px 12px', borderRadius: '10px', fontSize: '12px',
      lineHeight: '1.4', textDecoration: 'none',
      backgroundColor: lic.read_only ? '#2a0a0a' : '#2a1a0a',
      border: `1px solid ${lic.read_only ? '#e94560' : '#ffaa00'}`,
      color: lic.read_only ? '#ff8095' : '#ffcc66',
    }}>
      {lic.message}
    </Link>
  );
}

export function Sidebar({ active }) {
  const navigate = useNavigate();
  const role = localStorage.getItem('admin_role');
  const visibleNav = navItems
    .map(item => (isDemo() && item.path === '/benchmark-mode' ? { ...item, path: '/benchmarks' } : item))
    .filter(item => (!item.adminOnly || role === 'admin') && (!isDemo() || DEMO_PAGES.includes(item.path)));

  function logout() {
    localStorage.clear();
    navigate('/login');
  }

  return (
    <div style={styles.sidebar}>
      <div style={styles.sidebarLogo}>EZ PACE</div>
      <div style={styles.sidebarSubtitle}>
        {role === 'manager' ? 'Manager View' : 'Admin Dashboard'}
      </div>
      {visibleNav.map(item => (
        <Link
          key={item.path}
          to={item.path}
          style={active === item.label ? styles.navItemActive : styles.navItem}
        >
          {item.label}
        </Link>
      ))}
      {ONPREM && <LicenseNotice />}
      {isDemo() ? <DemoControls /> : (
        <button style={styles.logoutButton} onClick={logout}>
          Sign Out
        </button>
      )}
    </div>
  );
}

function Dashboard() {
  const name = localStorage.getItem('admin_name');
  const token = localStorage.getItem('admin_token');
  const [stats, setStats] = React.useState(null);
  // Existing customers accept the current terms once, here.
  const [terms, setTerms] = React.useState(null);
  const [accepting, setAccepting] = React.useState(false);

  React.useEffect(() => {
    fetch(`${API}/admin/terms-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(setTerms)
      .catch(() => {});
  }, [token]);

  async function acceptTerms() {
    setAccepting(true);
    try {
      const res = await fetch(`${API}/admin/accept-terms`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ accepted: true, terms_version: legal.version }),
      });
      if (res.ok) setTerms(t => ({ ...t, needs_acceptance: false }));
    } catch (e) {}
    setAccepting(false);
  }

  React.useEffect(() => {
    fetch(`${API}/admin/stats`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => setStats(data))
      .catch(() => {});
  }, [token]);

  return (
    <div style={styles.container}>
      <Sidebar active="Dashboard" />
      <div style={styles.main}>
        {terms && terms.needs_acceptance && (
          <div style={{ backgroundColor: '#16213e', border: '2px solid #ffcc00', borderRadius: '12px', padding: '18px 20px', marginBottom: '24px' }}>
            <div style={{ color: '#ffcc00', fontSize: '16px', fontWeight: 'bold', marginBottom: '6px' }}>
              Please review our Terms of Service and Privacy Policy
            </div>
            <div style={{ color: '#d0d0e0', fontSize: '14px', lineHeight: 1.5, marginBottom: '12px' }}>
              {terms.accepted_version
                ? `We have updated our terms (version ${terms.current_version}). Please review and accept them to continue using EZ Pace.`
                : `Please review and accept our terms (version ${terms.current_version}) for your casino.`}
              {' '}
              <a href="/terms" target="_blank" rel="noreferrer" style={{ color: '#e94560', fontWeight: 'bold' }}>Terms of Service</a>
              {' · '}
              <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: '#e94560', fontWeight: 'bold' }}>Privacy Policy</a>
            </div>
            <button
              style={{ padding: '10px 20px', backgroundColor: '#e94560', border: 'none', borderRadius: '8px', color: '#ffffff', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
              disabled={accepting}
              onClick={acceptTerms}
            >
              {accepting ? 'Saving...' : 'I accept on behalf of my casino'}
            </button>
          </div>
        )}

        <div style={styles.pageTitle}>Welcome, {name}</div>
        <div style={styles.pageSubtitle}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>

        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{stats ? stats.total_sessions : '—'}</div>
            <div style={styles.statLabel}>Total Sessions</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{stats ? stats.active_dealers : '—'}</div>
            <div style={styles.statLabel}>Active Dealers</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{stats ? stats.observers : '—'}</div>
            <div style={styles.statLabel}>Observers</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{stats ? stats.sessions_this_week : '—'}</div>
            <div style={styles.statLabel}>Sessions This Week</div>
          </div>
        </div>

        <div style={styles.sectionTitle}>Quick Actions</div>
        <Link to="/dealers" style={styles.quickCard}>
          <div>
            <div style={styles.quickCardTitle}>Manage Dealers</div>
            <div style={styles.quickCardSubtitle}>Add, upload or deactivate dealers</div>
          </div>
          <div style={styles.quickCardArrow}>›</div>
        </Link>
        {!isDemo() && <Link to="/users" style={styles.quickCard}>
          <div>
            <div style={styles.quickCardTitle}>Manage Users</div>
            <div style={styles.quickCardSubtitle}>Add observers and admins</div>
          </div>
          <div style={styles.quickCardArrow}>›</div>
        </Link>}
        <Link to={isDemo() ? '/benchmarks' : '/benchmark-mode'} style={styles.quickCard}>
          <div>
            <div style={styles.quickCardTitle}>Set Benchmarks</div>
            <div style={styles.quickCardSubtitle}>Configure target times per game type</div>
          </div>
          <div style={styles.quickCardArrow}>›</div>
        </Link>
        <Link to="/reports" style={styles.quickCard}>
          <div>
            <div style={styles.quickCardTitle}>Reports</div>
            <div style={styles.quickCardSubtitle}>View team performance and trends</div>
          </div>
          <div style={styles.quickCardArrow}>›</div>
        </Link>
      </div>
    </div>
  );
}

export default Dashboard;